import { useState, useMemo, useEffect } from "react";
import { axInst } from "../../../Components/Login/Service";
import WaterMark from "../../../Components/WaterMark/WaterMark";
import { useQuery } from "@tanstack/react-query";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Loader } from "rsuite";
import { useSelector } from "react-redux";

const financierMatrixApi = async (body) => {
  try {
    const res = await axInst.post("dealer-review/financier_matrix", body);
    return res.data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

const FinancierMatrix = ({ productLine: staticProductLine }) => {
  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");

  const { data: filters, submitted } = useSelector(
    (state) => state.filtersDealerReview
  );

  const isOtherFilterApplied = (filters) => {
    const keys = Object.keys(filters).filter((k) => k !== "Product_Line");
    return keys.some((key) => filters[key]?.length > 0);
  };

  const hasProductLineRedux = filters?.Product_Line?.length > 0;
  const otherFiltersApplied = isOtherFilterApplied(filters);

  const finalProductLine = useMemo(() => {
    if (staticProductLine) return [staticProductLine];
    if (hasProductLineRedux) return filters.Product_Line;
    return selectedOption ? [selectedOption] : [];
  }, [staticProductLine, hasProductLineRedux, selectedOption, filters]);

  const {
    data: financierMatrixData = [],
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["financierMatrix", submitted, finalProductLine],
    queryFn: () =>
      financierMatrixApi({
        ...filters,
        Product_Line: finalProductLine,
      }),
    enabled: true,
    cacheTime: 0,
    staleTime: 0,
  });

  const handleSelectionChange = (event) => {
    const value = event.target.value;
    setSelectedOption(value);
  };

  const fetchOptions = async (filters) => {
    try {
      const response = await axInst.post("/dealer-review/gtme_filter", filters);
      const dropdownOptions = response?.data || [];
      setOptions(dropdownOptions);

      if (!hasProductLineRedux && !otherFiltersApplied) {
        setSelectedOption("");
      }

      if (hasProductLineRedux) {
        setSelectedOption(filters.Product_Line[0]);
      }
    } catch (err) {
      console.error("Dropdown fetch error:", err);
    }
  };

  useEffect(() => {
    if (!staticProductLine) fetchOptions(filters);
  }, [submitted]);

  // ──────────────── Pivot dynamically ────────────────
  const pivotData = useMemo(() => {
    // Ensure it's an array and has at least one object
    if (!Array.isArray(financierMatrixData) || financierMatrixData.length === 0)
      return [];

    // Take the first object and only pivot if it has keys
    const firstItem = financierMatrixData[0];
    if (!firstItem || typeof firstItem !== "object") return [];

    const metrics = Object.keys(firstItem).filter((k) => k !== "FinancerName");

    return metrics.map((metric) => {
      const row = { metric };
      financierMatrixData.forEach((item) => {
        // Only assign if item has the key
        if (item && item.hasOwnProperty(metric)) {
          row[item.FinancerName] = item[metric];
        }
      });
      return row;
    });
  }, [financierMatrixData]);

  // ──────────────── Dynamic columns ────────────────
  const columns = useMemo(() => {
    if (pivotData.length === 0) return [];

    const financerNames = Object.keys(pivotData[0]).filter(
      (k) => k !== "metric"
    );

    return [
      {
        header: "Metric",
        accessorKey: "metric",
        cell: (info) => (
          <span className="font-semibold">{info.getValue()}</span>
        ),
      },
      ...financerNames.map((name) => ({
        header: name,
        accessorKey: name,
        cell: (info) => info.getValue() ?? "-",
      })),
    ];
  }, [pivotData]);

  const table = useReactTable({
    data: pivotData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const getRowBackgroundColor = (i) => ["#DBECFF", "#B0FAFF"][i % 2];

  return (
    <div className="relative shadow-lg rounded-lg p-2 bg-white">
      <div className="flex justify-between items-center">
        <header className="pb-2 text-lg font-bold text-gray-800">
          Financier Matrix {staticProductLine && `- ${staticProductLine}`}
        </header>

        {!staticProductLine && (
          <select
            value={selectedOption}
            onChange={handleSelectionChange}
            className="border border-gray-300 text-sm rounded-md w-40 p-1"
          >
            <option value="">All Product Line</option>
            {options.map((opt, idx) => (
              <option key={idx} value={opt.key}>
                {opt.key}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="self-center drilltable">
        {isError && (
          <div className="grid items-center justify-center w-full h-full">
            <p>Error Occurred</p>
          </div>
        )}

        {isFetching && (
          <div className="grid items-center justify-center w-full h-full">
            <Loader />
          </div>
        )}

        {/* ─────────────── Data Not Available ─────────────── */}
        {!isFetching && !isError && pivotData.length === 0 && (
          <div className="grid items-center justify-center w-full h-full">
            <p>Data Not Available</p>
          </div>
        )}

        {!isFetching && !isError && pivotData.length > 0 && (
          <>
            <WaterMark />

            <table className="w-full border-collapse border border-gray-300">
              <thead className="bg-[#2A5775] text-white">
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => (
                      <th
                        key={header.id}
                        className="p-2 text-[10px] border border-gray-300"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody>
                {table.getRowModel().rows.map((row, idx) => (
                  <tr
                    key={row.id}
                    style={{ backgroundColor: getRowBackgroundColor(idx) }}
                  >
                    {row.getVisibleCells().map((cell, cIdx) => (
                      <td
                        key={cell.id}
                        className={`p-2 border text-[12px] border-gray-300 ${
                          cIdx === 0 ? "text-left font-semibold" : "text-center"
                        }`}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default FinancierMatrix;
